import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Award, 
  BookOpen, 
  Calendar, 
  CheckCircle2, 
  ChevronDown, 
  Compass, 
  Flag, 
  GraduationCap,
  Heart, 
  Info, 
  Map, 
  Medal, 
  Star, 
  Target, 
  Users,
  Feather
} from 'lucide-react';
import { useTranslation } from '../Context/TranslationContext';

const BulbulSection = () => {
  const { isTamil } = useTranslation();
  const [expandedSection, setExpandedSection] = useState(null);

  const overviewContent = {
    intro: {
      en: "The Bulbul section is designed for girls aged 5+ to 10 years, focusing on character development, basic scouting skills, and personal growth through structured activities in the Bulbul Flock.",
      ta: "புல்புல் பிரிவு 5+ முதல் 10 வயது வரையிலான சிறுமிகளுக்காக வடிவமைக்கப்பட்டுள்ளது, கட்டமைக்கப்பட்ட செயல்பாடுகள் மூலம் குணாதிசய வளர்ச்சி, அடிப்படை சாரணர் திறன்கள் மற்றும் தனிப்பட்ட வளர்ச்சியில் கவனம் செலுத்துகிறது."
    },
    keyFeatures: [
      {
        title: { en: "Age Group", ta: "வயது பிரிவு" },
        content: { en: "5+ to 10 years", ta: "5+ முதல் 10 வயது வரை" },
        icon: Users
      },
      {
        title: { en: "Unit Name", ta: "அலகு பெயர்" },
        content: { en: "Bulbul Flock", ta: "புல்புல் ஃப்ளாக்" },
        icon: Flag
      },
      {
        title: { en: "Motto", ta: "குறிக்கோள்" },
        content: { en: "Do Your Best", ta: "சிறப்பாக செய்யுங்கள்" },
        icon: Target
      }
    ],
    objectives: [
      {
        title: { en: "Character Development", ta: "குணாதிசய வளர்ச்சி" },
        content: { 
          en: "Building moral values, leadership qualities, and good citizenship through guided activities",
          ta: "வழிகாட்டப்பட்ட செயல்பாடுகள் மூலம் நன்னெறி மதிப்புகள், தலைமைப் பண்புகள் மற்றும் நல்ல குடிமக்கள் உருவாக்கம்"
        }
      },
      {
        title: { en: "Skill Building", ta: "திறன் மேம்பாடு" },
        content: {
          en: "Learning essential life skills, crafts, and outdoor activities with progressive challenges",
          ta: "முன்னேற்ற சவால்களுடன் அத்தியாவசிய வாழ்க்கைத் திறன்கள், கைவினை மற்றும் வெளிப்புற செயல்பாடுகளைக் கற்றல்"
        }
      },
      {
        title: { en: "Social Growth", ta: "சமூக வளர்ச்சி" },
        content: {
          en: "Developing teamwork, communication skills, and community service mindset",
          ta: "குழு பணி, தொடர்பு திறன்கள் மற்றும் சமூக சேவை மனப்பான்மையை வளர்த்தல்"
        }
      }
    ]
  };

  const sectionTitles = {
    overview: {
      title: { en: "Overview", ta: "கண்ணோட்டம்" },
    },
    advancement: {
      title: { en: "Advancement Scheme", ta: "முன்னேற்றத் திட்டம்" },
    },
    proficiencyBadges: {
      title: { en: "Proficiency Badges", ta: "திறன் பதக்கங்கள்" },
    }
  };

  const advancementScheme = [
    {
      title: { en: "Pravesh (Entry)", ta: "பிரவேஷ்" },
      duration: { en: "3 months", ta: "3 மாதங்கள்" },
      requirements: [
        { en: "Tell the story of Tara", ta: "தாரா கதையை சொல்லுதல்" },
        { en: "Understand Bulbul Promise, Law, Motto and Greetings", ta: "புல்புல் வாக்குறுதி, சட்டம், குறிக்கோள் மற்றும் வாழ்த்துக்களை புரிந்துகொள்ளுதல்" },
        { en: "Demonstrate Bulbul Salute and Left Hand Shake", ta: "புல்புல் வணக்கம் மற்றும் இடது கை குலுக்கலை காட்டுதல்" },
        { en: "Daily prayers and good turn", ta: "தினசரி பிரார்த்தனை மற்றும் நல்ல செயல்" }
      ],
      icon: Flag
    },
    {
      title: { en: "Komal Pankh", ta: "கோமல் பங்க்" },
      duration: { en: "3 months", ta: "3 மாதங்கள்" },
      requirements: [
        { en: "Personal hygiene and tidiness", ta: "தனிப்பட்ட சுகாதாரம் மற்றும் சுத்தம்" },
        { en: "Physical activities and exercises", ta: "உடற்பயிற்சிகள் மற்றும் விளையாட்டுகள்" },
        { en: "Nature collection activities", ta: "இயற்கை சேகரிப்பு செயல்பாடுகள்" },
        { en: "Basic knots and their uses", ta: "அடிப்படை முடிச்சுகள் மற்றும் பயன்பாடுகள்" }
      ],
      icon: Feather
    },
    {
      title: { en: "Rajat Pankh", ta: "ரஜத் பங்க்" },
      duration: { en: "3 months", ta: "3 மாதங்கள்" },
      requirements: [
        { en: "Learn about local community", ta: "உள்ளூர் சமூகத்தைப் பற்றி கற்றல்" },
        { en: "Create handicrafts from waste", ta: "கழிவுப் பொருட்களில் கைவினை" },
        { en: "Basic first aid skills", ta: "அடிப்படை முதலுதவி திறன்கள்" },
        { en: "Environmental awareness", ta: "சுற்றுச்சூழல் விழிப்புணர்வு" }
      ],
      icon: Medal
    },
    {
      title: { en: "Swarna Pankh", ta: "ஸ்வர்ண பங்க்" },
      duration: { en: "9 months", ta: "9 மாதங்கள்" },
      requirements: [
        { en: "Advanced handicraft skills", ta: "மேம்பட்ட கைவினை திறன்கள்" },
        { en: "Compass and navigation", ta: "திசைகாட்டி மற்றும் வழிசெலுத்தல்" },
        { en: "Community service projects", ta: "சமூக சேவை திட்டங்கள்" },
        { en: "Earn proficiency badge", ta: "திறன் பதக்கம் பெறுதல்" }
      ],
      icon: Star
    },
    {
      title: { en: "Heerak Pankh", ta: "ஹீரக் பங்க்" },
      duration: { en: "9 months", ta: "9 மாதங்கள்" },
      requirements: [
        { en: "Overnight flock camp", ta: "இரவு முகாம் அனுபவம்" },
        { en: "Conservation projects", ta: "பாதுகாப்பு திட்டங்கள்" },
        { en: "Advanced knots and lashings", ta: "மேம்பட்ட முடிச்சுகள்" },
        { en: "Leadership activities", ta: "தலைமைத்துவ செயல்பாடுகள்" }
      ],
      icon: Award
    },
    {
      title: { en: "Golden Arrow", ta: "தங்க அம்பு" },
      special: true,
      requirements: [
        { en: "Hold Heerak Pankh", ta: "ஹீரக் பங்க் பெற்றிருத்தல்" },
        { en: "Earn six proficiency badges", ta: "ஆறு திறன் பதக்கங்கள் பெறுதல்" },
        { en: "Complete before age ten", ta: "பத்து வயதிற்குள் நிறைவு செய்தல்" }
      ],
      icon: Award
    }
  ];

  const proficiencyBadges = [
    {
      category: { en: "Foundational Skills", ta: "அடிப்படை திறன்கள்" },
      badges: [
        { name: { en: "Observer", ta: "கவனிப்பாளர்" }, icon: Flag },
        { name: { en: "Team Player", ta: "அணி வீரர்" }, icon: Users },
        { name: { en: "Home Craft", ta: "வீட்டு கைவினை" }, icon: Heart }
      ]
    },
    {
      category: { en: "Outdoor Skills", ta: "வெளிப்புற திறன்கள்" },
      badges: [
        { name: { en: "World Conservation", ta: "உலக பாதுகாப்பு" }, icon: Map },
        { name: { en: "First Aider", ta: "முதலுதவியாளர்" }, icon: Heart },
        { name: { en: "Gardener", ta: "தோட்டக்காரர்" }, icon: Target }
      ]
    },
    {
      category: { en: "Special Skills", ta: "சிறப்பு திறன்கள்" },
      badges: [
        { name: { en: "Heritage", ta: "பாரம்பரியம்" }, icon: BookOpen },
        { name: { en: "Free Being Me", ta: "சுதந்திரமான நான்" }, icon: Star },
        { name: { en: "Book Binder", ta: "புத்தக கட்டுநர்" }, icon: Info }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 mt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#4E94FF] to-[#3D7EFF] py-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <h1 className="text-5xl font-bold mb-6">
              {isTamil ? "புல்புல் பிரிவு" : "Bulbul Section"}
            </h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              {overviewContent.intro[isTamil ? 'ta' : 'en']}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4">
        {/* Overview Section */}
        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.overview.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#4E94FF] mx-auto"></div>
          </div>

          {/* Key Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {overviewContent.keyFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-[#4E94FF] bg-opacity-10 rounded-lg text-[#4E94FF]">
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold">
                    {feature.title[isTamil ? 'ta' : 'en']}
                  </h3>
                </div>
                <p className="text-gray-600">
                  {feature.content[isTamil ? 'ta' : 'en']}
                </p>
              </motion.div>
            ))}
          </div>
{/* Objectives */}
<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {overviewContent.objectives.map((objective, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <h3 className="text-xl font-semibold mb-4">
                  {objective.title[isTamil ? 'ta' : 'en']}
                </h3>
                <p className="text-gray-600">
                  {objective.content[isTamil ? 'ta' : 'en']}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Advancement Section */}
        <section className="py-16 bg-white">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.advancement.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#4E94FF] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advancementScheme.map((level, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-xl shadow-lg p-6 ${level.special ? 'border-2 border-yellow-500' : ''}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`p-3 rounded-lg ${level.special ? 'bg-yellow-50 text-yellow-600' : 'bg-[#4E94FF] bg-opacity-10 text-[#4E94FF]'}`}>
                    <level.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {level.title[isTamil ? 'ta' : 'en']}
                    </h3>
                    {level.duration && (
                      <p className="text-sm text-gray-500">
                        {level.duration[isTamil ? 'ta' : 'en']}
                      </p>
                    )}
                  </div>
                </div>
                <ul className="space-y-2">
                  {level.requirements.map((req, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        {req[isTamil ? 'ta' : 'en']}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Proficiency Badges Section */}
        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.proficiencyBadges.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#4E94FF] mx-auto"></div>
          </div>

          <div className="space-y-8">
            {proficiencyBadges.map((category, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-2xl font-semibold mb-6">
                  {category.category[isTamil ? 'ta' : 'en']}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {category.badges.map((badge, bIndex) => (
                    <div key={bIndex} className="flex items-center gap-4 bg-gray-50 rounded-lg p-4">
                      <div className="p-2 bg-[#4E94FF] bg-opacity-10 rounded-lg text-[#4E94FF]">
                        <badge.icon className="w-5 h-5" />
                      </div>
                      <span className="text-gray-700">
                        {badge.name[isTamil ? 'ta' : 'en']}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default BulbulSection;